import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ARTICLE_TYPE_LABEL,
  archiveArticle,
  deleteArticle,
  getArticle,
  publishArticle,
  type ArticleDetail,
} from "../api/knowledge";
import { ApiError } from "../api/client";
import { LessonMarkdown } from "../components/LessonMarkdown";
import { useCurrentUser } from "../hooks/useCurrentUser";

function canEdit(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

const STATUS_LABEL = {
  draft: "Черновик",
  published: "Опубликовано",
  archived: "Архив",
} as const;

export function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const id = Number(articleId);
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректная статья");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const a = await getArticle(id);
      setArticle(a);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError("Статья не найдена");
      else if (err instanceof ApiError && err.status === 403) setError("Нет доступа");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) return null;
  const editable = canEdit(user.roles);

  async function handlePublish() {
    if (!article) return;
    try {
      await publishArticle(article.id);
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleArchive() {
    if (!article) return;
    if (!confirm(`Перевести «${article.title}» в архив?`)) return;
    try {
      await archiveArticle(article.id);
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleDelete() {
    if (!article) return;
    if (!confirm(`Удалить статью «${article.title}»?`)) return;
    try {
      await deleteArticle(article.id);
      navigate("/kb");
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (loading && !article) {
    return <div className="mx-auto max-w-3xl px-4 py-12 text-center text-slate-400">Загрузка…</div>;
  }

  if (error || !article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <p className="text-slate-500">{error ?? "Статья не найдена"}</p>
        <Link to="/kb" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          ← в базу знаний
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <Link to="/kb" className="text-sm text-slate-500 hover:text-brand-accent">
        ← база знаний
      </Link>
      <header className="mt-2 mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900">{article.title}</h1>
            {article.article_type !== "article" && (
              <span className="badge badge-brand">
                {ARTICLE_TYPE_LABEL[article.article_type]}
              </span>
            )}
            {article.status !== "published" && (
              <span className="badge badge-warn">
                {STATUS_LABEL[article.status]}
              </span>
            )}
          </div>
          {article.category_name && (
            <p className="mt-1 text-xs text-slate-500">{article.category_name}</p>
          )}
        </div>
        {editable && (
          <div className="flex gap-2">
            <Link
              to={`/kb/articles/${article.id}/edit`}
              className="btn-secondary"
            >
              Редактировать
            </Link>
            {article.status === "draft" && (
              <button
                type="button"
                onClick={handlePublish}
                className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-green-700"
              >
                Опубликовать
              </button>
            )}
            {article.status === "published" && (
              <button
                type="button"
                onClick={handleArchive}
                className="btn-secondary"
              >
                В архив
              </button>
            )}
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
            >
              Удалить
            </button>
          </div>
        )}
      </header>

      {article.content_markdown ? (
        <article className="rounded-lg bg-white p-6 shadow-sm">
          <LessonMarkdown source={article.content_markdown} />
        </article>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          Содержания пока нет.
        </div>
      )}
    </div>
  );
}
